import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { selectStandardProps } from "@/utils/componentUtils";
import { formatLocaleDateTime } from "@/utils/formatUtils";
import { isJson, saveTextFile } from "@/utils/utils";
import { App as AntApp, Col, Modal, Row, Select } from "antd";
import { useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { useGetCalcRecordWebservicesLogs } from "../../../../../common/api/queries/calcRecords";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import { ModalSizes } from "../../../../../common/constants";
import { UUID } from "../../../../../typings/global";
import { WebserviceLog } from "../../types";

const getRequestResponseString = (log?: WebserviceLog): { request: string; response: string } => {
  if (!log) {
    return { request: "", response: "" };
  }

  const result = {
    request: log.request,
    response: log.response ?? "",
  };

  if (isJson(log.request)) {
    result.request = JSON.stringify(JSON.parse(log.request), undefined, 2);
  }

  if (log.response && isJson(log.response)) {
    result.response = JSON.stringify(JSON.parse(log.response), undefined, 2);
  }

  return result;
};

interface Props {
  id?: UUID;
  onCancelClick: () => void;
}

export const CalcRecordWebservicesLogsModal = ({ id, onCancelClick }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const [selectedLogIndex, setSelectedLogIndex] = useState<number>(0);

  const { data: record } = useGetCalcRecordWebservicesLogs(id ?? "");

  const requestResponseString = getRequestResponseString(record?.webservicesLogs[selectedLogIndex]);

  const handleCopyInfoMessageDisplay = () => {
    message.info(t("common.clipboardCopied"));
  };

  const createLogsTitle = () => {
    return (
      t("calc.records.titles.webserviceLogs") +
      ` - ${record?.user.name} / ` +
      (record?.createdAt ? formatLocaleDateTime(record?.createdAt) : "")
    );
  };

  const handleLogFileSave = (content: string, type: "request" | "response") => {
    saveTextFile(content, createLogsTitle() + " _ " + t("common." + type));
  };

  return (
    <Modal
      open={!!record}
      title={createLogsTitle()}
      width={ModalSizes.HUGE}
      centered
      onCancel={onCancelClick}
      afterClose={() => setSelectedLogIndex(0)}
      footer={null}
    >
      <Row gutter={16} className="margin-bottom-medium">
        <Select
          {...selectStandardProps}
          style={{ width: "200px" }}
          value={selectedLogIndex}
          options={record?.webservicesLogs.map((log, index) => ({
            value: index,
            label: log.institution + (log.coverage ? " - " + log.coverage : ""),
          }))}
          onChange={setSelectedLogIndex}
        />
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <h3>{t("common.request")}</h3>

          <pre className="modal-code-block">{requestResponseString.request}</pre>

          <div className="center-align margin-top-small">
            <span className="margin-right-medium">
              <SecondaryButton
                icon={<AntIcon type="download" />}
                onClick={() => handleLogFileSave(requestResponseString.request, "request")}
              >
                {t("common.download")}
              </SecondaryButton>
            </span>

            <CopyToClipboard text={requestResponseString.request} onCopy={handleCopyInfoMessageDisplay}>
              <SecondaryButton icon={<AntIcon type="copy" />}>{t("common.copy")}</SecondaryButton>
            </CopyToClipboard>
          </div>
        </Col>

        <Col span={12}>
          <h3>{t("common.response")}</h3>

          <pre className="modal-code-block">{requestResponseString.response}</pre>

          <div className="center-align margin-top-small">
            <span className="margin-right-medium">
              <SecondaryButton
                icon={<AntIcon type="download" />}
                onClick={() => handleLogFileSave(requestResponseString.response, "response")}
              >
                {t("common.download")}
              </SecondaryButton>
            </span>

            <CopyToClipboard text={requestResponseString.response} onCopy={handleCopyInfoMessageDisplay}>
              <SecondaryButton icon={<AntIcon type="copy" />}>{t("common.copy")}</SecondaryButton>
            </CopyToClipboard>
          </div>
        </Col>
      </Row>
    </Modal>
  );
};
